// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { authLogin } from "../auth/authClient";
import { APP_ROUTES } from "../utils/appRoutes";

const quotes = [
  {
    text: "The future belongs to those who believe in the beauty of their dreams.",
    author: "Eleanor Roosevelt"
  },
  {
    text: "All our dreams can come true, if we have the courage to pursue them.",
    author: "Walt Disney"
  },
  {
    text: "Dream big and dare to fail.",
    author: "Norman Vaughan"
  },
  {
    text: "The only limit to our realization of tomorrow will be our doubts of today.",
    author: "Franklin D. Roosevelt"
  },
  {
    text: "Everything you can imagine is real.",
    author: "Pablo Picasso"
  },
  {
    text: "Don't watch the clock; do what it does. Keep going.",
    author: "Sam Levenson"
  },
  {
    text: "It always seems impossible until it's done.",
    author: "Nelson Mandela"
  },
  {
    text: "Your time is limited, don't waste it living someone else's life.",
    author: "Steve Jobs"
  }
];

const Home = () => {
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const loginTriggered = useRef(false);

  // Auto-login: unauthenticated visitors go straight to Auth0.
  // AuthBootstrap and App show recovery instead after a failed/lost session.
  useEffect(() => {
    if (isLoading || isAuthenticated) return;

    if (!loginTriggered.current) {
      loginTriggered.current = true;
      authLogin();
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
        setIsTransitioning(false);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#c8e8ff',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: '3px solid rgba(0,136,255,0.2)',
            borderTopColor: '#0088FF',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  if (isAuthenticated) return <Navigate to={APP_ROUTES.dashboard} replace />;

  const currentQuote = quotes[currentQuoteIndex];

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background: '#c8e8ff',
        fontFamily: "'Lato', system-ui, sans-serif",
      }}
    >
      {/* Decorative background — mirrors public Hero exactly */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -left-32 top-24 w-72 h-72 rounded-full opacity-30" style={{ background: '#a8d8f8' }} />
        <div className="absolute -right-20 top-16 w-56 h-56 rounded-full opacity-25" style={{ background: '#90ccf0' }} />
        <div className="absolute left-[12%] top-16 w-2 h-2 rounded-full" style={{ background: '#0088FF', opacity: 0.5 }} />
        <div className="absolute right-[18%] top-24 w-1.5 h-1.5 rounded-full" style={{ background: '#0088FF', opacity: 0.4 }} />
        <div className="absolute left-[22%] top-36 w-1 h-1 rounded-full" style={{ background: '#74D8F7', opacity: 0.6 }} />
        <div className="absolute right-[28%] top-12 w-1 h-1 rounded-full" style={{ background: '#74D8F7', opacity: 0.5 }} />
        <div className="absolute left-[8%] top-20 w-40 h-40 rounded-full" style={{ border: '1px solid rgba(0,136,255,0.10)' }} />
        <div className="absolute right-[10%] top-10 w-56 h-56 rounded-full" style={{ border: '1px solid rgba(0,136,255,0.10)' }} />
        <div className="absolute left-[30%] bottom-0 w-32 h-32 rounded-full" style={{ border: '1px solid rgba(0,136,255,0.10)' }} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
        {/* Logo */}
        <div className="mb-12">
          <img src="/dreamsup_logo.svg" alt="DreamsUp" className="h-14 w-auto" />
        </div>

        {/* Quote section */}
        <div
          className="text-center mb-12"
          style={{ maxWidth: '640px', width: '100%', minHeight: '148px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            style={{
              transition: 'opacity 0.5s ease, transform 0.5s ease',
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'translateY(8px)' : 'translateY(0)',
            }}
          >
            <p
              style={{
                fontFamily: "'Google Sans', 'Lato', system-ui, sans-serif",
                fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                fontWeight: 500,
                color: 'rgba(10,10,10,0.80)',
                lineHeight: 1.65,
                fontStyle: 'italic',
                marginBottom: '0.75rem',
              }}
            >
              "{currentQuote.text}"
            </p>
            <p
              style={{
                fontFamily: "'Lato', system-ui, sans-serif",
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#0088FF',
                letterSpacing: '0.01em',
              }}
            >
              — {currentQuote.author}
            </p>
          </div>
        </div>

        {/* Redirecting to sign in indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              border: '3px solid rgba(0,136,255,0.2)',
              borderTopColor: '#0088FF',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <span
            style={{
              fontFamily: "'Lato', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'rgba(10,10,10,0.55)',
            }}
          >
            Redirecting to sign in…
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>

        {/* Quote indicator dots */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '2rem', alignItems: 'center' }}>
          {quotes.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentQuoteIndex(index);
                  setIsTransitioning(false);
                }, 500);
              }}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                flexShrink: 0,
                background: index === currentQuoteIndex ? '#74D8F7' : '#ffffff',
                transition: 'background 0.3s ease',
              }}
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-5">
        <div
          style={{
            maxWidth: '1152px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <img src="/dreamsup_logo.svg" alt="DreamsUp" style={{ height: '20px', width: 'auto', opacity: 0.55 }} />
          <p
            style={{
              fontFamily: "'Lato', system-ui, sans-serif",
              fontSize: '0.75rem',
              color: 'rgba(10,10,10,0.45)',
              margin: 0,
            }}
          >
            Product of Dreams &amp; Goals Pte Ltd
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
